import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
    Loader2, Send, AtSign, MessageSquare, CheckCircle2, Circle,
    Trash2, Lightbulb, HelpCircle, AlertTriangle, FileText, Pencil,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { key: 'note', label: 'Note', icon: FileText, color: 'text-slate-700 bg-slate-100' },
    { key: 'decision', label: 'Décision', icon: CheckCircle2, color: 'text-secondary bg-secondary-light' },
    { key: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-700 bg-blue-50' },
    { key: 'risk', label: 'Risque', icon: AlertTriangle, color: 'text-red-700 bg-red-50' },
];
const CAT_BY_KEY = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

// Highlights @mentions in body text.
function renderBody(body, mentionedNames = new Set()) {
    if (!body) return null;
    return body.split(/(@[\p{L}0-9._-]+)/gu).map((part, i) => {
        if (part.startsWith('@')) {
            const isKnown = mentionedNames.has(part.slice(1).toLowerCase());
            return (
                <span
                    key={i}
                    className={isKnown
                        ? 'inline-block px-1.5 py-0.5 rounded bg-primary-light text-primary font-medium text-[0.95em]'
                        : 'text-primary/80'}
                >
                    {part}
                </span>
            );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}

export default function ProjectDiscussion({ projectId }) {
    const { user, profile, session } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ all: 0, unresolved: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');  // 'all' | 'unresolved' | category
    const [members, setMembers] = useState([]);
    const [draft, setDraft] = useState('');
    const [draftCategory, setDraftCategory] = useState('note');
    const [submitting, setSubmitting] = useState(false);
    const textareaRef = useRef(null);
    const [autocomplete, setAutocomplete] = useState({ open: false, query: '', start: 0 });
    const [editingId, setEditingId] = useState(null);
    const [editingBody, setEditingBody] = useState('');

    const authHeader = useCallback(() => ({
        headers: { Authorization: `Bearer ${session?.access_token}` },
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const qs = filter === 'all'
                ? ''
                : filter === 'unresolved'
                    ? '?unresolved_only=true'
                    : `?category=${filter}`;
            const r = await axios.get(`${API}/projects/${projectId}/discussions${qs}`, authHeader());
            setItems(r.data.items || []);
            setCounts(r.data.counts || { all: 0, unresolved: 0 });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Chargement échoué');
        } finally {
            setLoading(false);
        }
    }, [projectId, filter, authHeader]);

    useEffect(() => { load(); }, [load]);

    // Load mentionable members once
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/projects/${projectId}/members-mentionable`, authHeader());
                if (!cancelled) setMembers(r.data.items || []);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [projectId, authHeader]);

    const mentionedNames = useMemo(
        () => new Set(members.map(m => (m.full_name || '').toLowerCase().replace(/\s+/g, ''))),
        [members],
    );

    // Resolve mention strings to user IDs
    const extractMentionIds = useCallback((text) => {
        const matches = text.match(/@[\p{L}0-9._-]+/gu) || [];
        const ids = new Set();
        for (const m of matches) {
            const handle = m.slice(1).toLowerCase().replace(/\s+/g, '');
            const found = members.find(mb => (mb.full_name || '').toLowerCase().replace(/\s+/g, '') === handle);
            if (found) ids.add(found.id);
        }
        return Array.from(ids);
    }, [members]);

    // Watch for @ in textarea
    const onDraftChange = (e) => {
        const value = e.target.value;
        setDraft(value);
        const pos = e.target.selectionStart;
        const before = value.slice(0, pos);
        const at = before.lastIndexOf('@');
        if (at >= 0) {
            const afterAt = before.slice(at + 1);
            if (!afterAt.includes(' ') && !afterAt.includes('\n') && afterAt.length <= 20) {
                setAutocomplete({ open: true, query: afterAt.toLowerCase(), start: at });
                return;
            }
        }
        setAutocomplete({ open: false, query: '', start: 0 });
    };

    const insertMention = (member) => {
        const handle = (member.full_name || '').replace(/\s+/g, '');
        const newText = draft.slice(0, autocomplete.start) + `@${handle} ` + draft.slice(autocomplete.start + 1 + autocomplete.query.length);
        setDraft(newText);
        setAutocomplete({ open: false, query: '', start: 0 });
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const autocompleteSuggestions = useMemo(() => {
        if (!autocomplete.open) return [];
        const q = autocomplete.query;
        return members
            .filter(m => (m.full_name || '').toLowerCase().includes(q))
            .slice(0, 6);
    }, [autocomplete, members]);

    const submit = async () => {
        const body = draft.trim();
        if (!body) return;
        setSubmitting(true);
        try {
            const mentionedIds = extractMentionIds(body);
            await axios.post(
                `${API}/projects/${projectId}/discussions`,
                { body, category: draftCategory, mentioned_user_ids: mentionedIds },
                authHeader(),
            );
            setDraft('');
            setDraftCategory('note');
            toast.success(mentionedIds.length > 0
                ? `Message publié et ${mentionedIds.length} personne(s) notifiée(s)`
                : 'Message publié');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Publication échouée');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleResolved = async (msg) => {
        try {
            await axios.patch(
                `${API}/projects/${projectId}/discussions/${msg.id}`,
                { is_resolved: !msg.is_resolved },
                authHeader(),
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Action refusée');
        }
    };

    const startEdit = (msg) => {
        setEditingId(msg.id);
        setEditingBody(msg.body);
    };

    const saveEdit = async (msg) => {
        try {
            await axios.patch(
                `${API}/projects/${projectId}/discussions/${msg.id}`,
                { body: editingBody.trim() },
                authHeader(),
            );
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Modification refusée');
        }
    };

    const remove = async (msg) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        try {
            await axios.delete(`${API}/projects/${projectId}/discussions/${msg.id}`, authHeader());
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Suppression refusée');
        }
    };

    const FILTERS = [
        { key: 'all', label: 'Tous', count: counts.all },
        { key: 'unresolved', label: 'Non résolus', count: counts.unresolved },
        ...CATEGORIES.map(c => ({ key: c.key, label: c.label, count: counts[c.key] || 0 })),
    ];

    return (
        <div className="space-y-6" data-testid="project-discussion">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-xl">Discussion projet</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        Hub collaboratif — décisions, questions, notes, risques.
                        Utilisez <code className="text-xs bg-surface-muted px-1 rounded">@nom</code> pour notifier un membre.
                    </p>
                </div>
            </div>

            {/* Composer */}
            <div className="card-soft p-4 space-y-3 relative" data-testid="discussion-composer">
                <Textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={onDraftChange}
                    placeholder="Que voulez-vous partager ? Tapez @ pour mentionner un membre…"
                    rows={3}
                    className="resize-none"
                    data-testid="discussion-input"
                />
                {autocomplete.open && autocompleteSuggestions.length > 0 && (
                    <div
                        className="absolute z-10 left-4 top-[88px] w-72 rounded-md border border-border bg-white shadow-lg max-h-56 overflow-y-auto"
                        data-testid="mention-autocomplete"
                    >
                        {autocompleteSuggestions.map(m => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => insertMention(m)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-surface-muted flex items-center gap-2"
                                data-testid={`mention-option-${m.id}`}
                            >
                                <AtSign size={12} className="text-primary" />
                                <span className="font-medium">{m.full_name}</span>
                                <span className="text-xs text-muted-foreground ml-auto">{m.role || '—'}</span>
                            </button>
                        ))}
                    </div>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-1" data-testid="category-selector">
                        {CATEGORIES.map(c => {
                            const active = draftCategory === c.key;
                            const Icon = c.icon;
                            return (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => setDraftCategory(c.key)}
                                    data-testid={`category-${c.key}`}
                                    className={`inline-flex items-center gap-1 h-7 px-2.5 rounded text-xs font-medium transition-colors ${
                                        active ? `${c.color} ring-1 ring-current/20` : 'text-muted-foreground hover:bg-surface-muted'
                                    }`}
                                >
                                    <Icon size={11} />{c.label}
                                </button>
                            );
                        })}
                    </div>
                    <Button
                        onClick={submit}
                        disabled={submitting || !draft.trim()}
                        size="sm"
                        className="bg-primary hover:bg-primary-hover"
                        data-testid="discussion-submit"
                    >
                        {submitting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Send size={13} className="mr-1.5" />}
                        Publier
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2" data-testid="discussion-filters">
                {FILTERS.map(f => (
                    <button
                        key={f.key}
                        type="button"
                        onClick={() => setFilter(f.key)}
                        data-testid={`filter-${f.key}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                            filter === f.key
                                ? 'bg-ink text-white border-ink'
                                : 'bg-white text-muted-foreground border-border hover:border-ink/40'
                        }`}
                    >
                        {f.label}
                        {f.count > 0 && (
                            <span className={`ml-1.5 text-[10px] ${filter === f.key ? 'opacity-80' : 'text-ink/70'}`}>· {f.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Messages list */}
            {loading ? (
                <div className="py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-border rounded-xl bg-surface-muted/30" data-testid="discussion-empty">
                    <MessageSquare className="mx-auto mb-3 text-muted-foreground" size={26} />
                    <p className="text-sm text-muted-foreground">
                        {filter === 'unresolved' ? 'Aucun point en suspens. Tout est traité 🎉'
                            : filter === 'all' ? 'Aucun message — lancez la conversation !'
                                : `Aucun message dans cette catégorie.`}
                    </p>
                </div>
            ) : (
                <ul className="space-y-3" data-testid="discussion-list">
                    {items.map(msg => {
                        const cat = CAT_BY_KEY[msg.category] || CAT_BY_KEY.note;
                        const Icon = cat.icon;
                        const isMine = msg.user_id === user?.id;
                        const isAdmin = !!currentWorkspace?.is_admin || !!profile?.is_super_admin;
                        const canEdit = isMine || isAdmin;
                        return (
                            <li
                                key={msg.id}
                                className={`p-4 rounded-xl border bg-white transition-colors ${
                                    msg.is_resolved
                                        ? 'border-border opacity-60'
                                        : 'border-border hover:border-ink/20'
                                }`}
                                data-testid={`discussion-msg-${msg.id}`}
                            >
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${cat.color}`}>
                                            <Icon size={10} />{cat.label}
                                        </span>
                                        <span className="text-xs font-medium text-ink">{msg.author_name}</span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {new Date(msg.created_at).toLocaleString('fr-FR')}
                                        </span>
                                        {msg.is_resolved && (
                                            <span className="text-[10px] text-secondary font-medium inline-flex items-center gap-1">
                                                <CheckCircle2 size={10} /> Résolu
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => toggleResolved(msg)}
                                            className="h-7 w-7 rounded text-muted-foreground hover:text-secondary hover:bg-surface-muted flex items-center justify-center"
                                            title={msg.is_resolved ? 'Réouvrir' : 'Marquer comme résolu'}
                                            data-testid={`msg-toggle-resolved-${msg.id}`}
                                        >
                                            {msg.is_resolved ? <Circle size={12} /> : <CheckCircle2 size={12} />}
                                        </button>
                                        {canEdit && editingId !== msg.id && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(msg)}
                                                    className="h-7 w-7 rounded text-muted-foreground hover:text-ink hover:bg-surface-muted flex items-center justify-center"
                                                    data-testid={`msg-edit-${msg.id}`}
                                                ><Pencil size={11} /></button>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(msg)}
                                                    className="h-7 w-7 rounded text-muted-foreground hover:text-destructive hover:bg-surface-muted flex items-center justify-center"
                                                    data-testid={`msg-delete-${msg.id}`}
                                                ><Trash2 size={11} /></button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {editingId === msg.id ? (
                                    <div className="space-y-2">
                                        <Textarea
                                            value={editingBody}
                                            onChange={(e) => setEditingBody(e.target.value)}
                                            rows={3}
                                            data-testid={`msg-edit-input-${msg.id}`}
                                        />
                                        <div className="flex justify-end gap-2">
                                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Annuler</Button>
                                            <Button size="sm" onClick={() => saveEdit(msg)} className="bg-primary hover:bg-primary-hover" data-testid={`msg-edit-save-${msg.id}`}>
                                                Enregistrer
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed pl-1">
                                        {renderBody(msg.body, mentionedNames)}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
